import { useNavigate } from 'react-router-dom';
import { Flame, Activity, Snowflake, Trophy, MapPin, Send, Eye, MessageSquareReply, MailX, MousePointerClick, Target, ArrowUpRight } from 'lucide-react';

export const money = v => (v ? `$${Math.round(v).toLocaleString()}` : '—');

export function Panel({ title, icon: Icon, accent = '#3B82F6', action, className = '', children, testId }) {
  return (
    <section data-testid={testId} className={`dash-panel relative rounded-2xl border border-[#1F293D] bg-[#111827] p-5 transition-[border-color,transform] duration-200 hover:border-[#334155] ${className}`}>
      <div className="pointer-events-none absolute inset-x-6 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }} />
      <header className="mb-4 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
          {Icon && <span className="grid h-6 w-6 place-items-center rounded-md" style={{ background: `${accent}22` }}><Icon className="h-3.5 w-3.5" style={{ color: accent }} /></span>}
          {title}
        </h3>
        {action}
      </header>
      {children}
    </section>
  );
}

const TIERS = [
  { key: 'Hot', icon: Flame, color: '#EF4444' },
  { key: 'Active', icon: Activity, color: '#10B981' },
  { key: 'Cold', icon: Snowflake, color: '#06B6D4' },
];

export function BuyerTiersWidget({ data }) {
  const total = data.total || 0;
  return (
    <Panel title="Buyer Network" icon={Flame} accent="#EF4444" testId="widget-buyer-tiers" className="lg:col-span-4">
      <div className="flex items-end justify-between">
        <p className="text-5xl font-bold tracking-tight text-white tabular-nums" data-testid="buyer-total">{total}</p>
        <span className="rounded-full bg-emerald-500/15 px-2.5 py-1 text-xs font-semibold text-emerald-300" data-testid="buyer-new-month">+{data.new_this_month || 0} this month</span>
      </div>
      <p className="mt-1 text-xs text-slate-500">cash buyers in your database</p>
      <div className="mt-5 space-y-3.5">
        {TIERS.map(({ key, icon: I, color }) => {
          const n = data.tiers?.[key] || 0;
          const pct = total ? Math.round((n / total) * 100) : 0;
          return (
            <div key={key} data-testid={`tier-${key.toLowerCase()}`}>
              <div className="mb-1.5 flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-slate-300"><I className="h-3.5 w-3.5" style={{ color }} />{key}</span>
                <span className="tabular-nums text-slate-400"><b className="text-white">{n}</b> · {pct}%</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-[#1E293B]">
                <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${pct}%`, background: color, boxShadow: `0 0 12px ${color}` }} />
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

function BarList({ items, color, testId }) {
  const max = Math.max(1, ...items.map(i => i.purchases));
  if (!items.length) return <p className="py-4 text-xs text-slate-500">No location data yet</p>;
  return (
    <div className="space-y-2" data-testid={testId}>
      {items.map(i => (
        <div key={i.name} className="relative overflow-hidden rounded-lg bg-[#161F30] px-3 py-1.5 text-xs">
          <div className="absolute inset-y-0 left-0 opacity-25" style={{ width: `${(i.purchases / max) * 100}%`, background: color }} />
          <div className="relative flex justify-between"><span className="text-slate-200">{i.name}</span><span className="tabular-nums text-slate-400">{i.purchases} buy{i.purchases === 1 ? '' : 's'} · {i.buyers} buyer{i.buyers === 1 ? '' : 's'}</span></div>
        </div>
      ))}
    </div>
  );
}

export function TopBuyersWidget({ data }) {
  const navigate = useNavigate();
  const tierColor = { Hot: '#EF4444', Active: '#10B981', Cold: '#06B6D4' };
  return (
    <Panel title="Top Buyers & Hot Zips" icon={Trophy} accent="#F59E0B" testId="widget-top-buyers" className="lg:col-span-8">
      <div className="grid gap-5 md:grid-cols-2">
        <div className="space-y-1.5">
          {(data.top_buyers || []).length === 0 && <p className="text-xs text-slate-500">No buyers yet</p>}
          {(data.top_buyers || []).map((b, i) => (
            <button key={b.id} onClick={() => navigate(`/buyers/${b.id}`)} data-testid={`top-buyer-${b.id}`}
              className="group flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition-colors hover:bg-[#1A2436]">
              <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-lg text-xs font-bold ${i === 0 ? 'bg-amber-400 text-[#0B0F17]' : 'bg-[#1E293B] text-slate-300'}`}>{i + 1}</span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-white">{b.entity_name}</span>
                <span className="text-[11px] text-slate-500">{b.top_city || '—'} · avg {money(b.avg_price)}</span>
              </span>
              <span className="h-2 w-2 rounded-full" style={{ background: tierColor[b.tier] || '#64748B', boxShadow: `0 0 8px ${tierColor[b.tier] || '#64748B'}` }} />
              <span className="w-12 text-right text-sm font-semibold tabular-nums text-amber-300">{b.total_purchases_24mo || 0}<span className="text-[10px] text-slate-500"> buys</span></span>
            </button>
          ))}
        </div>
        <div className="space-y-4">
          <div><p className="mb-2 flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500"><MapPin className="h-3 w-3" />Top zips</p><BarList items={(data.top_zips || []).slice(0, 4)} color="#8B5CF6" testId="top-zips" /></div>
          <div><p className="mb-2 flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500"><MapPin className="h-3 w-3" />Top cities</p><BarList items={(data.top_cities || []).slice(0, 3)} color="#EC4899" testId="top-cities" /></div>
        </div>
      </div>
    </Panel>
  );
}

export function CampaignPerfWidget({ data }) {
  const c = data.campaigns || {};
  const pct = n => (c.sent ? Math.round((n / c.sent) * 100) : 0);
  const rows = [
    { k: 'sent', label: 'Sent', icon: Send, color: '#3B82F6', v: c.sent || 0, p: c.sent ? 100 : 0 },
    { k: 'opened', label: 'Opened', icon: Eye, color: '#8B5CF6', v: c.opened || 0, p: pct(c.opened) },
    { k: 'clicked', label: 'Clicked', icon: MousePointerClick, color: '#06B6D4', v: c.clicked || 0, p: pct(c.clicked) },
    { k: 'replied', label: 'Replied', icon: MessageSquareReply, color: '#10B981', v: c.replied || 0, p: pct(c.replied) },
    { k: 'bounced', label: 'Bounced', icon: MailX, color: '#EF4444', v: c.bounced || 0, p: pct(c.bounced) },
  ];
  return (
    <Panel title="Campaign Performance" icon={Send} accent="#8B5CF6" testId="widget-campaign-perf" className="lg:col-span-6">
      <div className="space-y-2.5">
        {rows.map(({ k, label, icon: I, color, v, p }) => (
          <div key={k} className="flex items-center gap-3" data-testid={`camp-${k}`}>
            <I className="h-4 w-4 shrink-0" style={{ color }} />
            <span className="w-16 text-xs text-slate-400">{label}</span>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-[#1E293B]"><div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${p}%`, background: `linear-gradient(90deg, ${color}99, ${color})` }} /></div>
            <span className="w-20 text-right text-xs tabular-nums text-slate-300"><b className="text-white">{v}</b> · {p}%</span>
          </div>
        ))}
      </div>
      <p className="mt-4 text-[11px] text-slate-500">{c.campaigns || 0} campaigns · {c.texted || 0} texts sent · {c.unsubscribed || 0} unsubscribed</p>
      {(data.recent_replies || []).length > 0 && (
        <div className="mt-3 space-y-1.5 border-t border-[#1F293D] pt-3" data-testid="recent-replies">
          {data.recent_replies.slice(0, 3).map(r => (
            <p key={r.id} className="truncate text-xs text-slate-400"><span className="mr-1.5 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] uppercase text-emerald-300">{r.channel}</span>{r.text}</p>
          ))}
        </div>
      )}
    </Panel>
  );
}

const scoreClass = s => (s >= 70 ? 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/40' : s >= 40 ? 'bg-amber-500/15 text-amber-300 ring-amber-500/40' : 'bg-slate-500/15 text-slate-300 ring-slate-500/30');

export function UcMatchesWidget({ data }) {
  const navigate = useNavigate();
  const items = data.uc_matches || [];
  return (
    <Panel title="Buyer Matches · Under Contract" icon={Target} accent="#10B981" testId="widget-uc-matches" className="lg:col-span-12">
      {items.length === 0 ? <p className="py-6 text-center text-sm text-slate-500" data-testid="uc-empty">No Under Contract leads right now. Matches show up here when a deal goes under contract.</p> : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {items.map(l => (
            <div key={l.lead_id} className="rounded-xl border border-[#1F293D] bg-[#0F1623] p-4" data-testid={`uc-lead-${l.lead_id}`}>
              <button onClick={() => navigate(`/leads/${l.lead_id}`)} className="group flex w-full items-start justify-between text-left">
                <span className="min-w-0"><span className="block truncate text-sm font-semibold text-white">{l.address}</span><span className="text-xs text-slate-500">{l.city} · {money(l.price)}</span></span>
                <ArrowUpRight className="h-4 w-4 text-slate-500 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-emerald-300" />
              </button>
              <div className="mt-3 space-y-1.5">
                {l.matches.length === 0 && <p className="text-xs text-slate-500">No matching buyers yet</p>}
                {l.matches.map(m => (
                  <button key={m.id} onClick={() => navigate(`/buyers/${m.id}`)} className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-[#1A2436]" data-testid={`uc-match-${l.lead_id}-${m.id}`}>
                    <span className={`rounded-md px-1.5 py-0.5 text-[11px] font-bold tabular-nums ring-1 ${scoreClass(m.score)}`}>{m.score}</span>
                    <span className="min-w-0 flex-1"><span className="block truncate text-xs font-medium text-slate-200">{m.entity_name}</span><span className="block truncate text-[10px] text-slate-500">{m.reasons.join(' · ')}</span></span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
